import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Compass, ArrowRight, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SILK_ROAD_REGIONS } from '@/data/silkRoadRegions';

export default function SilkRoadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${t('silkRoad.title')} | ArtCanvas`;
    return () => { document.title = 'ArtCanvas'; };
  }, [t]);

  return (
    <div className="min-h-screen bg-amber-50 pt-16" data-testid="silk-road-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[360px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/12895031/pexels-photo-12895031.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Silk Road"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-slate-950/20" />
        <div className="absolute inset-0 flex flex-col justify-end px-6 pb-12 md:pb-16">
          <div className="max-w-5xl mx-auto w-full">
            <div className="flex items-center gap-3 mb-4">
              <Compass className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm tracking-[0.2em] uppercase font-medium">
                {t('silkRoad.badge')}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="silk-road-page-title">
              {t('silkRoad.title')}
            </h1>
            <p className="text-white/70 text-lg max-w-3xl">
              {t('silkRoad.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Region Grid */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SILK_ROAD_REGIONS.map((region) => (
              <Link
                key={region.slug}
                to={`/explore/${region.slug}`}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] block"
                data-testid={`silk-road-region-${region.slug}`}
              >
                <img
                  src={region.heroImage}
                  alt={t(`silkRoad.regions.${region.slug}.name`)}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 group-hover:from-black/90 transition-all duration-500" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <p className="text-amber-400 text-xs tracking-[0.15em] uppercase font-medium mb-2">
                    {t(`silkRoad.regions.${region.slug}.tagline`)}
                  </p>
                  <h2 className="text-white text-2xl font-bold mb-2">
                    {t(`silkRoad.regions.${region.slug}.name`)}
                  </h2>
                  <p className="text-slate-300 text-sm leading-relaxed line-clamp-2 mb-3">
                    {t(`silkRoad.regions.${region.slug}.description`)}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-amber-400 text-sm font-medium group-hover:gap-3 transition-all duration-300">
                    {t('silkRoad.discoverMore')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="bg-slate-950 py-16 md:py-20 px-6" data-testid="silk-road-page-cta">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
            <Palette className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t('silkRoad.joinAsArtist')}
          </h2>
          <p className="text-slate-400 mb-8">
            {t('silkRoad.joinDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-amber-600 hover:bg-amber-500 text-white px-8 py-6 rounded-xl"
              onClick={() => navigate('/login')}
              data-testid="silk-road-page-join-btn"
            >
              <Palette className="w-5 h-5 mr-2" />
              {t('silkRoad.joinAsArtist')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-xl"
              onClick={() => navigate('/artcanvas')}
            >
              {t('silkRoad.exploreGallery')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
